import React, { useEffect, useState } from 'react';
import { Globe, CreditCard } from 'lucide-react';
import api from '@/lib/api';

function fmtDate(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';
}
const usd = (n) => { const v = Number(n || 0); return `$${v % 1 ? v.toFixed(2) : v}`; };

// Pro ended: the website and the web address are no longer included. Two
// choices, both through the regular website plan checkout.
function RenewalChoiceCard({ domain }) {
  const tld = (domain.domain_name || '').split('.').pop();
  const [quote, setQuote] = useState(null);
  const [billing, setBilling] = useState('annual');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let off = false;
    api.getPlans(tld).then(r => { if (!off) setQuote(r.data); }).catch(() => {});
    return () => { off = true; };
  }, [tld]);

  const keep = quote?.website;           // website + this domain
  const free = quote?.subdomain;         // website only
  const half = billing === 'half_year';
  const keepTotal = keep ? (half ? keep.half_year_usd : keep.annual_usd) : null;
  const freeTotal = free ? (half ? free.half_year_usd : free.annual_usd) : (half ? 6 : 12);
  const per = half ? '/6 months' : '/yr';
  // Removal date = 90 days after Pro ended (server: LAPSE_DELETE_DAYS).
  const flagged = domain.renewal_flagged_at ? new Date(domain.renewal_flagged_at) : null;
  const deadline = flagged && !Number.isNaN(flagged.getTime()) ? new Date(flagged.getTime() + 90 * 86400000) : null;
  const when = deadline ? fmtDate(deadline.toISOString()) : '';
  const daysLeft = deadline ? Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 86400000)) : null;

  const choose = async (choice) => {
    setBusy(choice); setError('');
    try {
      const res = await api.startPlanCheckout({
        billing, keep_domain_id: domain.id, renewal_choice: choice, origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not open payment. Please try again.');
      setBusy('');
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 sm:p-6" data-testid={`renewal-due-${domain.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <Globe className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Your Pro plan covered your website and web address.</p>
          <p className="text-sm text-slate-300 mt-1 break-words">Choose how to keep your website going.</p>
          {when && (
            <p className="text-sm text-amber-200 mt-2 break-words" data-testid="renewal-deadline">
              If you don't choose by <strong className="text-white">{when}</strong>, your site will be removed.
              {daysLeft !== null && <span className="ml-2 inline-block rounded-md bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-200" data-testid="renewal-countdown">{daysLeft === 1 ? '1 day left' : `${daysLeft} days left`}</span>}
            </p>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4" role="radiogroup" aria-label="How often to pay">
        {[['annual', 'Yearly'], ['half_year', 'Every 6 months']].map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={billing === v} onClick={() => setBilling(v)}
            className={`flex-1 min-h-[44px] rounded-lg text-sm font-medium border ${billing === v ? 'border-emerald-500 bg-emerald-500/15 text-white' : 'border-slate-700 text-slate-300'}`}
            data-testid={`renewal-billing-${v}`}>{label}</button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-700 bg-slate-900/50 p-4 flex flex-col" data-testid="renewal-option-keep">
          <p className="text-sm font-semibold text-white break-words">Keep {domain.domain_name}</p>
          <p className="text-xs text-slate-400 mt-1">
            {keep ? (half
              ? <>Website {usd(keep.website_usd / 2)} + domain {usd(keep.domain_usd / 2)} = <strong className="text-white">{usd(keepTotal)}{per}</strong></>
              : <>Website {usd(keep.website_usd)} + domain {usd(keep.domain_usd)} = <strong className="text-white">{usd(keepTotal)}{per}</strong></>)
              : 'Loading price…'}
          </p>
          <button type="button" onClick={() => choose('keep')} disabled={!keep || !!busy}
            className="mt-auto pt-0 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white min-h-[48px] rounded-lg text-sm font-semibold mt-4"
            data-testid="renewal-choose-keep">
            <CreditCard className="w-4 h-4" /> {busy === 'keep' ? 'Opening payment…' : keep ? `Pay ${usd(keepTotal)} and keep it` : 'Pay and keep it'}
          </button>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900/50 p-4 flex flex-col" data-testid="renewal-option-free">
          <p className="text-sm font-semibold text-white">Use a free web address</p>
          <p className="text-xs text-slate-400 mt-1">
            Website only: <strong className="text-white">{usd(freeTotal)}{per}</strong>. Your site moves to a free address of ours and {domain.domain_name} isn't renewed.
          </p>
          <button type="button" onClick={() => choose('free_address')} disabled={!!busy}
            className="w-full inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-slate-400 text-white min-h-[48px] rounded-lg text-sm font-semibold mt-4"
            data-testid="renewal-choose-free">
            {busy === 'free_address' ? 'Opening payment…' : `Pay ${usd(freeTotal)} for the website`}
          </button>
        </div>
      </div>
      {error && <p className="text-xs text-red-400 mt-3">{error}</p>}
    </div>
  );
}

export default function RenewalDueCards({ domains }) {
  const due = (domains || []).filter(d => d?.renewal_status === 'due');
  if (!due.length) return null;
  return <div className="space-y-4 mb-8">{due.map(d => <RenewalChoiceCard key={d.id} domain={d} />)}</div>;
}
